import styles from "./appointmentsPage.module.css";
import "../../styles/index.css";
import React from "react";
import { Outlet, useLocation } from "react-router-dom";
import TabNavigation from "./components/TabNavigation/TabNavigation";
import AppointmentFilters from "./components/AppointmentFilters/AppointmentFilters";
import { VenchyAssistant } from "../../components/VenchyAssistant";
import { useAppSelector } from "../../services/hooks";
import { isSuperAdminByRoleId } from "../../services/selectors/admin.selectors";
import {
  AppointmentsProvider,
  useAppointmentsContext,
} from "./context/AppointmentsContext";

/**
 * Appointments Calendar Content
 *
 * Internal component that renders shared filters above the active view.
 * Filters stay mounted and don't reset when switching between daily/weekly/monthly.
 */
function AppointmentsCalendarContent() {
  const location = useLocation();
  const {
    therapistsList,
    companiesOptions,
    fundingSourcesList,
    filters,
    setFilters,
    isFullscreen,
    toggleFullscreen,
    showFiltersInFullscreen,
    setShowFiltersInFullscreen,
  } = useAppointmentsContext();

  // Determine if we're in a calendar view
  const isCalendarView = location.pathname.includes("/calendar/");

  if (!isCalendarView) {
    // For non-calendar views (no-show predictor, conflicts detector), render as before
    return <Outlet />;
  }

  return (
    <div
      className={
        isFullscreen
          ? `${styles.fullscreenOverlay} ${!showFiltersInFullscreen ? styles.fullscreenFiltersHidden : ""}`
          : undefined
      }
    >
      {isFullscreen && (
        <button
          type="button"
          className={styles.fullscreenCloseBtn}
          onClick={toggleFullscreen}
          aria-label="סגור מסך מלא"
        >
          ✕
        </button>
      )}
      {isFullscreen && (
        <button
          type="button"
          className={styles.fullscreenFiltersToggle}
          onClick={() => setShowFiltersInFullscreen((v) => !v)}
          aria-label={showFiltersInFullscreen ? "הסתר מסננים" : "הצג מסננים"}
          title={showFiltersInFullscreen ? "הסתר מסננים" : "הצג מסננים"}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: showFiltersInFullscreen ? "rotate(180deg)" : undefined,
            }}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      )}
      {/* Shared Filters - shown in normal mode; in fullscreen only when toggled */}
      {(!isFullscreen || showFiltersInFullscreen) && (
        <AppointmentFilters
          filters={filters}
          onFiltersChange={setFilters}
          therapists={therapistsList}
          companies={companiesOptions}
          fundingSources={fundingSourcesList}
        />
      )}

      {/* Active view content (Daily/Weekly/Monthly) - each view handles its own stats */}
      <Outlet />
    </div>
  );
}

/**
 * Appointments Management Page
 *
 * Main container for the appointments scheduling system.
 * Features three main tabs:
 * - Calendar: Daily, Weekly, Monthly views for appointment management
 * - No-Show Predictor: AI-powered predictions for missed appointments (Super Admin only)
 * - Conflicts Detector: Smart conflict detection and resolution (Super Admin only)
 *
 * Uses sub-routes for different views: calendar/daily, calendar/weekly, calendar/monthly, no-show-predictor, conflicts-detector
 *
 * Includes Venchy Assistant - a floating chat helper for appointment management (Super Admin only)
 *
 * Wraps all views with AppointmentsProvider to share data and caching across Daily/Weekly/Monthly views.
 */
function AppointmentsPage() {
  const isSuperAdminUser = useAppSelector(isSuperAdminByRoleId);

  return (
    <AppointmentsProvider>
      <article className="menuSection">
        <div className={styles.container}>
          {/* Tab Navigation */}
          <TabNavigation />

          {/* Calendar content with shared filters & stats */}
          <AppointmentsCalendarContent />
        </div>

        {/* Venchy Assistant - Floating chat helper (only visible for Super Admin) */}
        {isSuperAdminUser && <VenchyAssistant />}
      </article>
    </AppointmentsProvider>
  );
}

export default AppointmentsPage;
